"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Aligned with the hygenius-pria reference select
 * (../hygenius-pria/src/components/ui/select.tsx) so the PRIA chat forms and
 * the rest of www share one primitive rather than a fork.
 *
 * Read this before syncing against the reference again — every deviation
 * below is deliberate:
 *
 * - `focus-visible:ring-ring` at full opacity, `hover:brightness-110` on the
 *   primary variant, `ring-offset-2` with `ring-offset-background`, and no
 *   `aria-invalid:*` or `dark:*`: all four for the reasons button.tsx spells
 *   out at length. Read that header once rather than re-deriving it per file;
 *   each aligned primitive draws whichever of those moves its own reference
 *   calls for.
 * - Tailwind v4 spellings are translated to their v3 equivalents:
 *   `shadow-xs` to `shadow-sm`, `outline-hidden` to `outline-none`,
 *   `data-disabled:` to `data-[disabled]:`, `max-h-(--var)` to
 *   `max-h-[var(--var)]`, and `*:[span]:last:x` to `[&>span:last-child]:x`.
 * - Stacked variants are also reordered, which is easy to miss because the
 *   class still compiles either way. v4 applies a stack left to right, v3
 *   right to left, so the reference's `hover:[&>svg]:!text-white` means
 *   `.trigger:hover > svg` there and `.trigger > svg:hover` here — the chevron
 *   would only turn white while the pointer was over the chevron itself. The
 *   outline variant therefore writes `[&>svg]:hover:!text-white`. Same-element
 *   stacks like `hover:data-[placeholder]:` need no reordering.
 * - The reference clamps the trigger's value with four rules
 *   (`line-clamp-1 flex items-center gap-2`); only `line-clamp-1` survives
 *   here, as `[&>span]`. Tailwind 3 emits `.flex` after `.line-clamp-1`, so
 *   its `display:flex` beats `display:-webkit-box` and kills the
 *   `-webkit-line-clamp` — leaving `overflow:hidden` to clip long CMS facet
 *   labels with no ellipsis. The flex row only matters for a value containing
 *   an icon beside its text, which nothing in www or PRIA renders. Selecting
 *   `> span` rather than the reference's `[data-slot=select-value]` is what
 *   lets SelectValue stay a plain re-export: Radix always renders Value as a
 *   span, and the chevron beside it is an svg.
 * - `size` stays a cva variant (www's shape) rather than the reference's
 *   `data-size` attribute. The call-site API is the same `size="sm"`, and a
 *   plain class is what tailwind-merge needs to let a call site override the
 *   height with one of its own — webform--lead-gen's `h-[34px]` depends on it.
 * - `data-[disabled]:opacity-50` is kept on SelectItem, which the reference
 *   dropped. www's product and document filters render disabled facet options
 *   (widget--product-grid, widget--document-search, widget--resources-section)
 *   and `pointer-events-none` alone leaves them looking selectable.
 * - SelectItem adds `outline-0`, which the reference does not need. Tailwind's
 *   `outline-none` is a 2px *transparent* outline, and www's global
 *   `*:focus { outline-color: var(--primary) }` (styles/globals.css) recolours
 *   it — so the highlighted item grew a brand-blue outline, clipped by the
 *   dropdown edge. Zeroing the width is what actually removes it; the item's
 *   own focus signal is the `focus:bg-accent` fill below.
 * - SelectItem keeps `focus:bg-accent` where the reference sets
 *   `focus:bg-transparent`. The reference signals the highlighted option with
 *   brand-blue text alone; AGENTS.md requires every state to carry a signal
 *   other than colour, and brand blue measures 2.53:1 on white, so the fill
 *   stays and the reference's `focus:text-primary` sits on top of it. That
 *   brand text sits below AGENTS.md's 16px/semibold floor — accepted for
 *   parity, as the outline trigger's brand label already is; the fill and the
 *   check indicator are the non-colour signals the floor exists to guarantee.
 *
 * `outline` and `primary` are PRIA trigger variants; `default` is the neutral
 * form-field trigger every non-chat call site uses.
 */
const selectTriggerVariants = cva(
  "flex w-fit items-center justify-between gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm shadow-sm outline-none ring-offset-background transition-[color,background-color,border-color,filter,box-shadow] focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-[3px] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "cursor-pointer border-input bg-transparent data-[placeholder]:text-muted-foreground [&>svg]:text-muted-foreground [&>svg]:opacity-50",
        outline:
          "cursor-pointer border-2 border-primary bg-white font-medium text-primary hover:bg-primary hover:!text-white hover:data-[placeholder]:!text-white [&>svg]:hover:!text-white data-[placeholder]:text-primary [&>svg]:text-primary",
        primary:
          "cursor-pointer border-transparent bg-primary text-white hover:brightness-110 data-[placeholder]:text-white [&>svg]:text-white [&>svg]:opacity-100",
      },
      size: {
        default: "h-9",
        sm: "h-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, size, variant, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ size, variant }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="size-4 transition-colors" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUpIcon className="size-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDownIcon className="size-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(
  (
    { className, children, position = "popper", align = "center", ...props },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-[var(--radix-select-content-available-height)] min-w-[8rem] origin-[var(--radix-select-content-transform-origin)] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-xs text-muted-foreground", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none outline-0 focus:bg-accent focus:text-primary data-[state=checked]:text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&>span:last-child]:flex [&>span:last-child]:items-center [&>span:last-child]:gap-2",
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="size-4 text-primary" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  selectTriggerVariants,
}
